/**
 * Error Boundary Component
 */

"use client";

import { Button } from "@/components/ui/primitives/button";
import { Subheading } from "@/components/ui/primitives/heading";
import { Text } from "@/components/ui/primitives/text";
import { buildSessionExpiredSignInHref } from "@/lib/auth/redirects";
import { backendHealthTracker } from "@/lib/utils/backend-health-tracker";
import { displayError } from "@/lib/utils/error-display";
import { logger } from "@/lib/utils/logger";
import {
  handleServerActionError,
  isServerActionError,
} from "@/lib/utils/server-action-error-handler";
import { Component, ErrorInfo, ReactNode } from "react";

function isAuthenticationError(error: Error): boolean {
  if ("status" in error && (error as { status: number }).status === 401) {
    return true;
  }
  const msg = error.message?.toLowerCase() ?? "";
  return (
    msg.includes("authentication required") || msg.includes("unauthorized")
  );
}

export interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Auth errors: redirect to sign in immediately instead of showing error UI
    if (isAuthenticationError(error)) {
      logger.warn(
        "Authentication error caught by ErrorBoundary, redirecting to sign in",
        {
          url:
            globalThis.window === undefined
              ? undefined
              : globalThis.location.href,
        },
      );
      if (globalThis.window !== undefined) {
        const current =
          globalThis.window.location.pathname +
          globalThis.window.location.search;
        const href = buildSessionExpiredSignInHref(current);
        if (href) {
          globalThis.location.href = href;
        }
      }
      return;
    }

    // Log error to PostHog (production) or console (development)
    logger.error("React Error Boundary caught error", error, {
      componentStack: errorInfo.componentStack,
      errorName: error.name,
      url:
        globalThis.window === undefined ? undefined : globalThis.location.href,
    });

    // Check for Server Action mismatch errors first (highest priority)
    if (isServerActionError(error)) {
      const refreshed = handleServerActionError(error);
      if (refreshed) {
        // Page will refresh, no need to show error UI
        return;
      }
      // If refresh failed or max attempts reached, continue to show error UI
    }

    // Track network errors for maintenance mode detection
    if (
      error.name === "NetworkError" ||
      error.name === "TimeoutError" ||
      error.message?.includes("fetch failed") ||
      error.message?.includes("network")
    ) {
      backendHealthTracker.recordFailure("ErrorBoundary");

      // Check if we should enter maintenance mode (async operation handled separately)
      if (backendHealthTracker.shouldCheckMaintenance()) {
        // Perform async check without blocking
        backendHealthTracker
          .performFinalPingCheck()
          .then((isBackendHealthy) => {
            if (!isBackendHealthy) {
              // Class component outside the router; full navigation is required.
              // eslint-disable-next-line @next/next/no-location-assign-relative-destination
              globalThis.location.href = "/maintenance";
            }
          })
          .catch((err) => {
            // Log ping check failure
            logger.error(
              "Ping check failed",
              err instanceof Error ? err : undefined,
              {
                source: "ErrorBoundary",
              },
            );
          });
      }
    }
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Auth errors: render nothing while componentDidCatch handles redirect
      if (isAuthenticationError(this.state.error)) {
        return null;
      }

      // Check if this is a Server Action error
      const isServerActionErr = isServerActionError(this.state.error);

      return (
        <div
          className="my-4 rounded-lg border border-red-200 bg-red-50 p-6"
          role="alert"
        >
          <div className="space-y-4">
            <div>
              <Subheading className="text-red-800">
                {isServerActionErr
                  ? "Application Update Detected"
                  : "Something went wrong"}
              </Subheading>
              <Text className="mt-2 text-red-700">
                {isServerActionErr
                  ? "The application has been updated. Please refresh the page to continue."
                  : displayError(
                      this.state.error,
                      "An unexpected error occurred.",
                    )}
              </Text>
            </div>
            <div className="flex gap-2">
              {isServerActionErr ? (
                <Button
                  onClick={() => globalThis.location.reload()}
                  color="red"
                >
                  Refresh Page
                </Button>
              ) : (
                <Button onClick={this.reset} color="red">
                  Try Again
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
